import { Injectable } from '@angular/core';
import {
  CapacitorSQLite,
  capSQLiteUpgradeOptions,
  SQLiteConnection,
  SQLiteDBConnection
} from '@capacitor-community/sqlite';
import { Buffer } from 'buffer';

const dbName = 'all_values';
const currentDbVersion = 2;
const allValuesSchemaAndUpgrades: capSQLiteUpgradeOptions = {
  database: dbName,
  upgrade: [
    {
      statements: [
        `CREATE TABLE IF NOT EXISTS allValues (key TEXT PRIMARY KEY NOT NULL, value TEXT, type INTEGER);`
      ],
      toVersion: 1
    },
    {
      statements: [
        `ALTER TABLE allValues ADD COLUMN blob BLOB;`
      ],
      toVersion: 2
    }
  ]
};

@Injectable({
  providedIn: 'root'
})
export class SqlStorageService {

  private sqlite = new SQLiteConnection(CapacitorSQLite);
  private db!: SQLiteDBConnection;

  constructor() { }

  public async initDatabase(): Promise<boolean> {
    await CapacitorSQLite.addUpgradeStatement(allValuesSchemaAndUpgrades);
    this.db = await this.sqlite.createConnection(dbName, false, 'no-encryption', currentDbVersion, false);
    await this.db.open();
    // await this.db.execute('DROP TABLE allValues');
    // const schema = `CREATE TABLE IF NOT EXISTS allValues (key TEXT PRIMARY KEY NOT NULL, value TEXT, type INTEGER);`;
    // await this.db.execute(schema);
    return true;
  }

  public async get(key: string): Promise<any> {
    const data = await this.db.query(`SELECT value, type, blob FROM allValues WHERE key = '${key}'`);
    if (!data.values || data.values.length === 0) {
      return undefined;
    }
    return this.deserialize(data.values[0].value, data.values[0].type, data.values[0].blob);
  }

  public async set(key: string, value: any, type: SqlStorageDataType): Promise<boolean> {
    const alreadyExists = await this.checkIfKeyExists(key);
    return alreadyExists ? this.update(key, value, type) : this.insert(key, value, type);
  }

  public async remove(key: string): Promise<void> {
    await this.db.query(`DELETE FROM allValues WHERE key = '${key}'`);
    return;
  }

  private async checkIfKeyExists(key: string): Promise<boolean> {
    const result = await this.db.query(`SELECT key FROM allValues WHERE key = '${key}'`);
    return result.values && result.values.length > 0;
  }

  private async insert(key: string, value: any, type: SqlStorageDataType): Promise<boolean> {
    const stringOrBlobValue = await this.serialize(value, type);
    const bindValues = this.generateBindValues(key, stringOrBlobValue, type, false);
    const query = `INSERT INTO allValues (key, value, type, blob) VALUES (?, ?, ?, ?)`;
    const result = await this.db.run(query, bindValues, true);
    return !!result.changes;
  }

  private async update(key: string, value: any, type: SqlStorageDataType): Promise<boolean> {
    const stringOrBlobValue = await this.serialize(value, type);
    const bindValues = this.generateBindValues(key, stringOrBlobValue, type, true);
    const query = `UPDATE allValues SET value = ?, type = ?, blob = ? WHERE key = ?`;
    const result = await this.db.run(query, bindValues, true);
    return !!result.changes;
  }

  private generateBindValues(key: string, stringOrBlobValue: string | Buffer, type: SqlStorageDataType, forUpdate: boolean):
      any[] {
    const value = type !== SqlStorageDataType.blob && type !== SqlStorageDataType.jsonBlob ? stringOrBlobValue as string : '';
    const blob = type === SqlStorageDataType.blob || type === SqlStorageDataType.jsonBlob ? stringOrBlobValue as Buffer : null;
    return forUpdate ? [value, type, blob, key] : [key, value, type, blob];
  }

  private async deserialize(value: string, type: SqlStorageDataType, blob: number[]): Promise<any> {
    switch (type) {
      case SqlStorageDataType.boolean:
        return Boolean(value);
      case SqlStorageDataType.number:
        return Number(value);
      case SqlStorageDataType.string:
        return atob(value);
      case SqlStorageDataType.blob:
        return blob;
      case SqlStorageDataType.jsonBlob:
        return JSON.parse(await this.decompress(blob));
      default:
        return JSON.parse(decodeURIComponent(atob(value)));
    }
  }

  private async serialize(value: any, type: SqlStorageDataType): Promise<string | Buffer> {
    switch (type) {
      case SqlStorageDataType.boolean:
        return String(value);
      case SqlStorageDataType.number:
        return String(value);
      case SqlStorageDataType.string:
        return btoa(value);
      case SqlStorageDataType.blob:
        return value as Buffer;
      case SqlStorageDataType.jsonBlob:
        return await this.compress(JSON.stringify(value));
      default:
        return btoa(encodeURIComponent(JSON.stringify(value)));
    }
  }

  private async compress(input: string): Promise<Buffer> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);

    const cs = new CompressionStream('gzip');
    const writer = cs.writable.getWriter();

    writer.write(data).then();
    writer.close().then();

    const compressedStream = cs.readable;
    return Buffer.from(await new Response(compressedStream).arrayBuffer());
  }

  private async decompress(compressedBlob: number[]): Promise<string> {
    const ds = new DecompressionStream('gzip');
    const writer = ds.writable.getWriter();

    writer.write(Buffer.from(compressedBlob)).then();
    writer.close().then();

    const decompressedStream = ds.readable;
    const decompressedBlob = await new Response(decompressedStream).arrayBuffer();
    const decoder = new TextDecoder();
    return decoder.decode(decompressedBlob);
  }
}

export enum SqlStorageDataType {
  object = 0,
  boolean = 1,
  number = 2,
  string = 3,
  jsonBlob = 4,
  blob = 5
}
